<template>
  <section class="mx-auto max-w-[1180px] px-6 pb-44 pt-8 text-dark lg:px-4 lg:py-16">
    <div class="mb-6 flex flex-col gap-5 lg:mb-10">
      <a @click.prevent="goBack" href="#back" class="hidden items-center gap-2 text-[16px] font-semibold text-dark lg:inline-flex">
        <span class="text-lg leading-none">‹</span>
        <span class="underline underline-offset-4">Go back</span>
      </a>

      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-4">
          <span class="flex h-6 w-6 items-center justify-center rounded-full lg:h-16 lg:w-16 lg:bg-light">
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.1998 27.1998C8.94761 27.1998 7.93311 28.2143 7.93311 29.4665C7.93311 30.7186 8.94761 31.7331 10.1998 31.7331C11.4519 31.7331 12.4664 30.7186 12.4664 29.4665C12.4664 28.2143 11.4519 27.1998 10.1998 27.1998ZM25.4998 27.1998C24.2476 27.1998 23.2331 28.2143 23.2331 29.4665C23.2331 30.7186 24.2476 31.7331 25.4998 31.7331C26.7519 31.7331 27.7664 30.7186 27.7664 29.4665C27.7664 28.2143 26.7519 27.1998 25.4998 27.1998ZM8.25978 8.4998L10.1998 22.6665H25.4998C26.0664 22.6665 26.5648 22.3271 26.7799 21.8071L30.8599 12.1738C31.1659 11.4371 30.6212 10.6248 29.8223 10.6248H11.2346L10.9173 8.30147C10.8266 7.7348 10.3506 7.3248 9.78394 7.3248H5.66644C5.04361 7.3248 4.53311 7.8353 4.53311 8.45814C4.53311 9.08097 5.04361 9.59147 5.66644 9.59147H8.25978ZM11.5519 12.8915H28.1221L24.7464 20.3998H12.1556L11.5519 12.8915Z" fill="#3B3C4A"/>
            </svg>
          </span>
          <h1 class="text-xl font-semibold lg:text-4xl" v-if="!cartItems.length">Buy Tickets</h1>
          <h1 class="text-xl font-semibold lg:text-4xl" v-else>{{ title }}</h1>
        </div>
      </div>
    </div>

    <div v-if="visibleMessages.length" class="mb-6 space-y-3">
      <div
        v-for="(message, index) in visibleMessages"
        :key="`${message}-${index}`"
        class="rounded-2xl border border-dashed border-[#F5A623] bg-[#FFF8E8] px-4 py-3 text-[15px] text-dark"
      >
        {{ message }}
      </div>
    </div>

    <div v-if="catalogLoadFailed" class="mb-6 rounded-[28px] bg-light px-6 py-8">
      <LoadError @retry="loadCart" />
    </div>

    <div v-if="cartSummaryWarnings.length" class="mb-4 space-y-3">
      <alert-error
          v-for="(warning, index) in cartSummaryWarnings"
          :key="`${index}-${warning}`">
        {{ warning }}
      </alert-error>
    </div>

    <div class="grid gap-10 lg:gap-16 lg:items-start lg:grid-cols-[minmax(0,1fr)_320px]" id="cart-top">
      <div class="space-y-4 lg:space-y-8">
        <!--  Hide for now -->
        <div v-if="false && cartItems.length" class="mb-2">
          <p class="border border-dashed border-[#F54E4E] bg-[#FFF1F1] text-black px-4 py-3 leading-none">
            Tickets are reserved for <strong class="font-bold">{{ countdown }}</strong> minutes. <strong class="font-bold">Complete checkout to confirm</strong>.
          </p>
        </div>

        <article
          v-for="item in cartItems"
          :key="item.id"
          class="rounded-[22px] border border-dashed border-primary px-4 py-4 lg:rounded-none lg:border-0 lg:px-0 lg:py-6"
        >
          <div class="flex items-start justify-between gap-4 flex-wrap xs:flex-nowrap">
            <div class="flex min-w-0 gap-4 lg:items-center">
              <svg class="hidden shrink-0 lg:block" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.805 14.0918L19.4449 6.2168C20.5105 5.60156 21.9032 5.97474 22.5185 7.04035L23.5907 8.89757C23.7665 9.20203 23.7116 9.5991 23.4903 9.84864C23.0349 10.3957 22.9555 11.1723 23.307 11.7812C23.6762 12.4206 24.3884 12.7401 25.0899 12.6192C25.4167 12.5524 25.788 12.7033 25.9638 13.0078L27.0185 14.8346C27.6513 15.9306 27.2605 17.2929 26.1949 17.9081L12.555 25.7831C11.4589 26.416 10.1143 26.0556 9.48145 24.9596L8.42676 23.1328C8.25098 22.8283 8.27545 22.4489 8.52718 22.1817C8.95216 21.6522 9.062 20.8581 8.69286 20.2187C8.3413 19.6098 7.59865 19.3079 6.92757 19.4111C6.57037 19.4956 6.22949 19.327 6.05371 19.0226L4.98145 17.1654C4.36621 16.0997 4.70894 14.7246 5.805 14.0918ZM6.44286 16.3216L7.23388 17.6917C8.3852 17.7171 9.53905 18.3094 10.1543 19.375C10.7871 20.471 10.7231 21.7664 10.1694 22.7762L10.9429 24.1158C11.1011 24.3898 11.4372 24.4799 11.7113 24.3217L25.3512 16.4467C25.5947 16.3061 25.7152 15.9523 25.557 15.6783L24.7836 14.3387C23.6018 14.3309 22.4784 13.721 21.8456 12.625C21.2304 11.5594 21.2639 10.2815 21.8481 9.25418L21.057 7.8841C20.9164 7.64053 20.5322 7.53759 20.2887 7.67821L6.64875 15.5532C6.37474 15.7114 6.30224 16.078 6.44286 16.3216ZM20.2537 15.4926L18.5662 12.5698L11.7462 16.5073L13.4337 19.4301L20.2537 15.4926ZM10.0036 16.8639C9.70473 16.3463 9.86731 15.6435 10.4153 15.3271L18.2096 10.8271C18.7272 10.5283 19.4475 10.7213 19.7463 11.2389L21.9963 15.136C22.3128 15.6841 22.1022 16.374 21.5846 16.6728L13.7903 21.1728C13.2423 21.4892 12.57 21.3091 12.2536 20.761L10.0036 16.8639Z" fill="#3B3C4A"/>
              </svg>
              <div class="min-w-0 ">
                <h2 class="flex items-start gap-2 text-[17px] font-semibold leading-tight lg:text-2xl">
                  <button
                    type="button"
                    class="min-w-0 max-w-full truncate text-left underline underline-offset-4 lg:no-underline"
                    @click="editCartItem(item)"
                  >
                    {{ attractionTitle(item) }}
                  </button>
                  <AlertErrorIcon
                    v-if="isCartTimeConflictItem(item)"
                    class="mt-0.5 h-5 w-5 shrink-0 text-alert-error lg:hidden"
                  />
                </h2>
                <p class="mt-1 text-[15px] text-dark/80 lg:mt-2 lg:text-dark/70">{{ formatLongDate(item.time) }}, {{ formatTime(item.time) }}</p>
                <button
                  class="mt-2 inline-flex text-[15px] font-semibold text-dark underline underline-offset-4 lg:hidden"
                  type="button"
                  @click="editCartItem(item)"
                >
                  Edit date/time
                </button>
              </div>
            </div>
            <div class="flex shrink-0 flex-col items-end gap-2 md:flex-row md:items-center md:gap-4">
              <button
                class="hidden text-[15px] font-semibold text-dark underline underline-offset-4 lg:inline-flex"
                type="button"
                @click="editCartItem(item)"
              >
                Edit date/time
              </button>
              <button class="text-[15px] font-semibold text-dark underline underline-offset-4 flex gap-2 items-center"
                      @click="removeCartItem(item)">
                Remove
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.3477 4.6875C10.2422 4.6875 10.1719 4.75781 10.1016 4.82812L9.43359 5.8125H14.5312L13.8633 4.82812C13.8281 4.75781 13.7227 4.6875 13.6172 4.6875H10.3477ZM16.5703 5.8125H17.0273H18.75H19.0312C19.4883 5.8125 19.875 6.19922 19.875 6.65625C19.875 7.14844 19.4883 7.5 19.0312 7.5H18.6094L17.7656 18.9258C17.6602 20.1211 16.7109 21 15.5156 21H8.44922C7.25391 21 6.30469 20.1211 6.19922 18.9258L5.35547 7.5H4.96875C4.47656 7.5 4.125 7.14844 4.125 6.65625C4.125 6.19922 4.47656 5.8125 4.96875 5.8125H5.25H6.9375H7.39453L8.69531 3.87891C9.04688 3.35156 9.67969 3 10.3477 3H13.6172C14.2852 3 14.918 3.35156 15.2695 3.87891L16.5703 5.8125ZM16.9219 7.5H7.04297L7.88672 18.8203C7.92188 19.1016 8.16797 19.3125 8.44922 19.3125H15.5156C15.7969 19.3125 16.043 19.1016 16.0781 18.8203L16.9219 7.5Z" fill="#3B3C4A"/>
                </svg>
              </button>
            </div>
          </div>

          <div class="mt-6 space-y-3 lg:mt-5 lg:space-y-4">
            <div v-for="ticket in ticketRows(item)" :key="ticket.key" class="flex items-center justify-between gap-4 lg:border-b lg:border-dashed lg:border-primary lg:pb-4">
              <div class="flex w-full items-center justify-between gap-4 text-[15px] lg:hidden">
                <div class="font-semibold">{{ ticket.count }}x {{ ticket.label }}</div>
                <div>{{ formatCurrency(ticket.price * ticket.count) }}</div>
              </div>

              <div class="hidden w-full items-center justify-between gap-4 lg:flex">
                <div class="text-lg font-semibold flex-grow">{{ ticket.label }}</div>
                <div class="text-[18px] text-dark font-semibold">{{ formatCurrency(ticket.price) }}</div>
                <div class="flex items-center gap-3">
                  <button class="flex h-11 w-11 items-center justify-center rounded-full bg-light  text-[24px]" @click="adjustCartParticipant(item, ticket, -1)">-</button>
                  <span class="min-w-[18px] text-center text-[18px] font-semibold">{{ ticket.count }}</span>
                  <button class="flex h-11 w-11 items-center justify-center rounded-full bg-light text-[24px]" @click="adjustCartParticipant(item, ticket, 1)">+</button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="getExtraRows(item).length" class="mt-4 space-y-3">
            <h3 class="text-base font-semibold">Extras</h3>
            <div v-for="extra in getExtraRows(item)" :key="extra.key" class="flex items-start justify-between gap-4 text-[15px] lg:text-lg">
              <span>{{ extra.count }}x {{ extra.label }}</span>
              <span class="shrink-0">{{ formatCurrency(extra.total) }}</span>
            </div>
          </div>

          <div v-if="editingCartItemId === item.id" class="mt-6" :class="{'space-y-4': !fullscreenMode}">
            <div class="flex items-center justify-between gap-4">
              <h3 class="text-xl font-semibold">Edit date and time</h3>
              <button
                type="button"
                class="text-[15px] font-semibold text-dark underline underline-offset-4"
                @click="cancelEditCartItem"
              >
                Cancel
              </button>
            </div>

            <ExperienceCheckoutWidget
              :key="`edit-${item.id}-${inlineBookingKey}`"
              :code="cartItemAttractionCode(item)"
              :title="attractionTitle(item)"
              :auto-select-first="false"
              :cart-url="cartUrl"
              :checkout-url="checkoutUrl"
              :continue-url="continueUrl"
              :editing-item="item"
              :widget-id="`cart-edit-booking-${item.id}`"
              layout="cart"
              empty-state-message="This experience is not available for online booking right now."
              @updated-cart="handleInlineUpdated"
              @fullscreen-mode="handleFullscreenMode"
            />
          </div>
        </article>

        <div
          v-if="removedCartItem"
          class="flex items-center justify-between gap-4 rounded-full border border-dashed border-[#70D6AF] bg-[#E4F7F0] px-4 py-2 text-dark"
          role="status"
          aria-live="polite"
        >
          <div class="flex min-w-0 items-center gap-4">
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#70D6AF]">
              <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.5 2L8.25 14.25L2.5 8.5" stroke="#336552" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            <div class="truncate text-lg font-semibold md:text-xl">Removed from cart</div>
          </div>

          <button
            type="button"
            class="inline-flex shrink-0 items-center gap-3 text-[18px] font-semibold text-dark underline underline-offset-4 md:text-[20px]"
            @click="undoRemoveCartItem"
          >
            <span>Undo</span>
            <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M9.18164 11.625H3.875V6.31836" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M4.86133 16.9315C5.38567 19.8974 7.31435 22.4241 10.0333 23.7127C12.7523 25.0013 15.9258 24.8925 18.5494 23.4206C21.173 21.9488 22.9226 19.2958 23.2444 16.3044C23.5662 13.313 22.4205 10.3528 20.1701 8.35556C17.9198 6.35831 14.843 5.57077 11.914 6.24228C8.98504 6.91379 6.5661 8.9614 5.42383 11.7421" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div
          v-if="!cartItems.length"
          class="flex flex-col gap-4 border-y border-dashed border-primary px-5 py-5 text-dark md:flex-row md:items-center md:justify-between md:px-6 mb-20"
        >
          <h2 class="text-xl font-medium leading-tight">Not sure what to book?</h2>
          <a
            href="/visit-experiences/"
            class="text-xl font-medium leading-tight text-dark underline underline-offset-4 transition hover:text-secondary"
          >
            View All Experiences
          </a>
        </div>

        <div v-if="showExperienceSelector" class="space-y-6">
          <div class="relative max-w-[820px]" v-if="!fullscreenMode">
            <div class="flex items-center gap-4">
              <button
                type="button"
                class="flex flex-1 items-center justify-between gap-4 rounded-full bg-light pl-6 text-left text-lg text-dark shadow-sm transition hover:bg-primary-tone/30"
                @click="toggleAttractionPicker"
              >
                <span>{{ selectedAddAttractionLabel }}</span>
                <span class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-dark text-light">
                  <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 2L10 10L18 2" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </button>

              <button
                v-if="cartItems.length"
                type="button"
                class="inline-flex items-center gap-2 text-[15px] font-semibold text-dark underline underline-offset-4"
                @click="cancelAddExperience"
              >
                Remove
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.3477 4.6875C10.2422 4.6875 10.1719 4.75781 10.1016 4.82812L9.43359 5.8125H14.5312L13.8633 4.82812C13.8281 4.75781 13.7227 4.6875 13.6172 4.6875H10.3477ZM16.5703 5.8125H17.0273H18.75H19.0312C19.4883 5.8125 19.875 6.19922 19.875 6.65625C19.875 7.14844 19.4883 7.5 19.0312 7.5H18.6094L17.7656 18.9258C17.6602 20.1211 16.7109 21 15.5156 21H8.44922C7.25391 21 6.30469 20.1211 6.19922 18.9258L5.35547 7.5H4.96875C4.47656 7.5 4.125 7.14844 4.125 6.65625C4.125 6.19922 4.47656 5.8125 4.96875 5.8125H5.25H6.9375H7.39453L8.69531 3.87891C9.04688 3.35156 9.67969 3 10.3477 3H13.6172C14.2852 3 14.918 3.35156 15.2695 3.87891L16.5703 5.8125ZM16.9219 7.5H7.04297L7.88672 18.8203C7.92188 19.1016 8.16797 19.3125 8.44922 19.3125H15.5156C15.7969 19.3125 16.043 19.1016 16.0781 18.8203L16.9219 7.5Z" fill="#3B3C4A"/>
                </svg>
              </button>
            </div>

            <div
              v-if="attractionPickerOpen"
              class="absolute left-0 right-0 z-30 mt-3 max-h-[440px] overflow-auto rounded-[28px] bg-white px-6 py-5 text-[20px] shadow-[0_18px_40px_rgba(31,38,54,0.18)]"
            >
              <div v-if="catalogLoading" class="px-2 py-4 text-dark/60">
                Loading experiences...
              </div>
              <div v-else-if="!selectableAttractions.length" class="px-2 py-4 text-dark/60">
                No experiences are available for online booking right now.
              </div>
              <template v-else>
                <button
                  v-for="attraction in selectableAttractions"
                  :key="attraction.code"
                  type="button"
                  class="block w-full rounded-2xl px-2 py-3 text-left transition hover:bg-light"
                  :class="selectedAddAttractionCode === attraction.code ? 'font-semibold' : ''"
                  @click="chooseAddAttraction(attraction)"
                >
                  {{ attraction.label }}
                </button>
              </template>
            </div>
          </div>

          <ExperienceCheckoutWidget
            v-if="selectedAddAttractionCode"
            :key="`${selectedAddAttractionCode}-${inlineBookingKey}`"
            :code="selectedAddAttractionCode"
            :title="selectedAddAttraction?.title || ''"
            :auto-select-first="false"
            :cart-url="cartUrl"
            :checkout-url="checkoutUrl"
            :continue-url="continueUrl"
            layout="cart"
            widget-id="cart-inline-booking"
            empty-state-message="Choose an experience above to begin booking."
            @added-to-cart="handleInlineAdded"
            @fullscreen-mode="handleFullscreenMode"
          />
        </div>

        <button
          v-else
          type="button"
          class="flex w-full items-center justify-center rounded-full bg-light px-6 py-4 text-[18px] font-semibold text-dark transition hover:text-secondary lg:ml-auto lg:w-auto lg:gap-5 lg:bg-transparent lg:px-0 lg:py-0 lg:text-[20px]"
          @click="beginAddExperience"
        >
          <span class="lg:hidden">View more experiences</span>
          <span class="hidden lg:inline">Add another experience</span>
          <span class="hidden h-14 w-14 items-center justify-center rounded-full bg-primary text-[34px] font-normal leading-none text-dark lg:flex">
            <svg width="16" height="16" class="h-6 w-6" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8.92308 0.923077V7.07692H15.0769C15.5769 7.07692 16 7.5 16 8C16 8.53846 15.5769 8.92308 15.0769 8.92308H8.92308V15.0769C8.92308 15.6154 8.5 16 8 16C7.46154 16 7.07692 15.6154 7.07692 15.0769V8.92308H0.923077C0.384615 8.92308 0 8.53846 0 8C0 7.5 0.384615 7.07692 0.923077 7.07692H7.07692V0.923077C7.07692 0.423077 7.46154 0 8 0C8.5 0 8.92308 0.423077 8.92308 0.923077Z" fill="currentColor"/>
            </svg>

          </span>
        </button>

        <template v-if="cartItems.length">
          <CheckoutUpsellCard
            v-for="(offer, index) in cartUpsells"
            :key="offer.id ?? `${offer.add_on_attraction_id}-${index}`"
            :offer="offer"
            class="bg-light"
            text-class="text-dark"
          />
        </template>
      </div>

      <BookingSummary
        from="cart"
        class="hidden lg:sticky lg:top-24 lg:block"
        :checkout-url="checkoutUrl"
        :show-checkout-button="true"
        :warnings="cartSummaryWarnings"
      />
    </div>

    <div
      v-if="cartItems.length"
      class="fixed inset-x-0 bottom-0 z-[9000] bg-light px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-4 text-dark lg:hidden"
    >
      <div v-if="mobileUpsellDiscounts.length" class="mb-4 space-y-2 border-b border-dashed border-primary pb-3">
        <div
          v-for="(discount, index) in mobileUpsellDiscounts"
          :key="`${discount.id ?? index}-mobile-upsell-discount`"
          class="flex items-start justify-between gap-4 text-[15px]"
        >
          <div>{{ discount.title ?? 'Total discount' }}</div>
          <div class="font-medium">-{{ formatCurrency(discount.discount_value) }}</div>
        </div>
      </div>

      <div class="mb-4 flex items-start justify-between gap-4">
        <div class="text-[18px] font-medium">Order Subtotal</div>
        <div class="text-right">
          <div class="text-[18px] font-semibold">{{ formatCurrency(mobileCartSubtotal) }}</div>
          <div class="mt-1 text-xs">AUD (inc. GST)</div>
        </div>
      </div>

      <div class="flex items-center justify-between gap-4">
        <button
          type="button"
          class="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white text-3xl leading-none text-dark"
          @click="goBack"
        >
          ‹
        </button>

        <a
          :href="canCheckout ? checkoutUrl : '#'"
          class="inline-flex min-h-[54px] flex-1 items-center justify-center gap-3 rounded-full bg-primary px-6 text-[18px] font-semibold text-dark"
          :class="{ 'opacity-50': !canCheckout }"
        >
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M5.875 5.625V7.5H12.125V5.625C12.125 3.90625 10.7188 2.5 9 2.5C7.24219 2.5 5.875 3.90625 5.875 5.625ZM3.375 7.5V5.625C3.375 2.53906 5.875 0 9 0C12.0859 0 14.625 2.53906 14.625 5.625V7.5H15.25C16.6172 7.5 17.75 8.63281 17.75 10V17.5C17.75 18.9062 16.6172 20 15.25 20H2.75C1.34375 20 0.25 18.9062 0.25 17.5V10C0.25 8.63281 1.34375 7.5 2.75 7.5H3.375Z" fill="currentColor"/>
          </svg>
          <span>Checkout Now</span>
        </a>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { CART_RESERVATION_MINUTES } from './config'
import BookingSummary from './components/BookingSummary.vue'
import CheckoutUpsellCard from './components/CheckoutUpsellCard.vue'
import ExperienceCheckoutWidget from './ExperienceCheckoutWidget.vue'
import { useCheckoutCartStore } from './store'
import { bookingItemConflictKey, bookingItemsTooCloseKeys, bookingItemsTooCloseMessages, isEnabledFlag } from './helpers'
import {
  childTicketNeedsAdultOrSenior,
  childTicketValidationMessage,
  getExtraRows,
  resolveTicketOptions,
} from './helpers/cart'
import { formatCurrency, formatLongDate, formatTime, normaliseParticipantKey } from './helpers/format'
import { calculateLocalUpsellDiscounts } from './helpers/upsells'
import { createZohoChatVisibilityController } from './helpers/zoho-chat'
import { reportLoadError } from '../helpers/network'
import AlertError from "@/checkout/components/AlertError.vue";
import AlertErrorIcon from "@/checkout/components/AlertErrorIcon.vue";
import LoadError from './components/LoadError.vue'

const props = defineProps({
  code: {
    type: [String, Number, Boolean],
    default: false,
  },
  cartUrl: {
    type: String,
    default: '/cart/',
  },
  checkoutUrl: {
    type: String,
    default: '/checkout/',
  },
  continueUrl: {
    type: String,
    default: '/visit-experiences/',
  },
  title: {
    type: String,
    default: 'Cart',
  },
})

const store = useCheckoutCartStore()
const { cartItems, cartMessages, selectedUpsellOffers } = storeToRefs(store)
const countdown = ref(`${CART_RESERVATION_MINUTES}:00`)
const visibleMessages = ref([])
const catalogLoading = ref(true)
const catalogLoadFailed = ref(false)
const addingExperience = ref(false)
const selectedAddAttractionCode = ref(false)
const attractionPickerOpen = ref(false)
const fullscreenMode = ref(false)
const inlineBookingKey = ref(0)
const editingCartItemId = ref(null)
const removedCartItem = ref(null)
const removedCartItemIndex = ref(-1)
const cartUpsells = computed(() => store.upsellsByPlacement.cart ?? [])
const mobileUpsellDiscounts = computed(() => calculateLocalUpsellDiscounts(selectedUpsellOffers.value, cartItems.value))
const mobileUpsellDiscountTotal = computed(() => mobileUpsellDiscounts.value.reduce((total, discount) => {
  return total + Number(discount.discount_value ?? 0)
}, 0))
const mobileCartSubtotal = computed(() => Math.max(0, store.cartSubtotal - mobileUpsellDiscountTotal.value))
const mobileFooterHiddenClass = 'checkout-mobile-hide-footer'
const zohoChat = createZohoChatVisibilityController()
let countdownInterval = null

const experienceDetails = computed(() => {
  const widget = typeof window !== 'undefined' ? window.bookingWidget : null
  return Array.isArray(widget?.experiences) ? widget.experiences : []
})

const selectableAttractions = computed(() => {
  const detailsByCode = new Map(
    experienceDetails.value
      .filter((item) => item?.code)
      .map((item) => [item.code, item])
  )

  return store.attractions
    .filter((attraction) => attraction?.code && isEnabledFlag(attraction?.enabled_retail))
    .map((attraction) => {
      const details = detailsByCode.get(attraction.code) ?? {}
      const title = String(details.title ?? attraction.retail_title ?? attraction.title ?? attraction.code)
      // const location = String(details.short_address ?? attraction.retail_location ?? '')
      const location = String(attraction.retail_location ?? '')

      return {
        ...attraction,
        ...details,
        title,
        label: title,
        // label: location && !title.toLowerCase().includes(String(location).toLowerCase())
        //   ? `${title}, ${location}`
        //   : title,
        order: Number(attraction.order ?? details.order ?? 999),
      }
    })
    .sort((left, right) => {
      if (left.order !== right.order) {
        return left.order - right.order
      }

      return left.label.localeCompare(right.label)
    })
})

const showExperienceSelector = computed(() => addingExperience.value || cartItems.value.length === 0)
const selectedAddAttraction = computed(() => (
  selectableAttractions.value.find((attraction) => attraction.code === selectedAddAttractionCode.value) ?? null
))
const selectedAddAttractionLabel = computed(() => selectedAddAttraction.value?.title ?? '--Select experience--')
const cartTimeConflictWarnings = computed(() => bookingItemsTooCloseMessages(cartItems.value))
const cartHasTimeConflict = computed(() => cartTimeConflictWarnings.value.length > 0)
const cartChildTicketConflictItems = computed(() => cartItems.value.filter((item) => (
  childTicketNeedsAdultOrSenior(item, store.getAttraction(item.attraction))
)))
const cartHasChildTicketConflict = computed(() => cartChildTicketConflictItems.value.length > 0)
const childTicketCartWarning = computed(() => {
  if (!cartHasChildTicketConflict.value) {
    return ''
  }

  const titles = cartChildTicketConflictItems.value
    .map((item) => attractionTitle(item))
    .filter(Boolean)

  return titles.length
    ? `${childTicketValidationMessage} Please add an adult or senior ticket for ${titles.join(', ')}, or remove the child ticket.`
    : childTicketValidationMessage
})
const cartSummaryWarnings = computed(() => [
  ...cartTimeConflictWarnings.value,
  ...(childTicketCartWarning.value ? [childTicketCartWarning.value] : []),
])
const cartTimeConflictItemKeys = computed(() => (
  cartHasTimeConflict.value ? new Set(bookingItemsTooCloseKeys(cartItems.value)) : new Set()
))

const canCheckout = computed(() => !cartHasTimeConflict.value && !cartHasChildTicketConflict.value)

const isCartTimeConflictItem = (item) => cartTimeConflictItemKeys.value.has(bookingItemConflictKey(item))

const cartUpsellSignature = computed(() => JSON.stringify(
  cartItems.value.map((item) => ({
    attraction: item?.attraction,
    product_code: item?.product_code ?? '',
    time: item?.time ?? '',
    participants: item?.participants ?? {},
  }))
))

const syncCartMessages = () => {
  if (!cartMessages.value.length) {
    return
  }

  visibleMessages.value = [...visibleMessages.value, ...cartMessages.value]
  store.clearCartMessages()
}

const updateCountdown = () => {
  store.pruneUnavailableCart()
  syncCartMessages()

  if (store.pruneExpiredCart()) {
    countdown.value = '0:00'
    return
  }

  if (!cartItems.value.length) {
    countdown.value = '0:00'
    return
  }

  const nearest = Math.min(...cartItems.value.map((item) => Number(item.reservationExpiresAt ?? Date.now())))
  const diff = Math.max(0, nearest - Date.now())
  const minutes = Math.floor(diff / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)
  countdown.value = `${minutes}:${String(seconds).padStart(2, '0')}`
}

const attractionTitle = (item) => item.attractionTitle || store.getAttraction(item.attraction)?.retail_title || 'Experience'
const cartItemAttractionCode = (item) => store.getAttraction(item.attraction)?.code ?? ''

const ticketRows = (item) => resolveTicketOptions(item)
    .map((ticket) => {
      const key = normaliseParticipantKey(ticket.label)
      return {
        key,
        label: ticket.label,
        count: Number(item.participants?.[key] ?? 0),
        price: Number(ticket.price ?? 0),
      }
    })
    .filter((ticket) => {
      return ticket.count > 0
    })

const cloneCartItem = (item) => JSON.parse(JSON.stringify(item))

const clearRemovedCartItem = () => {
  removedCartItem.value = null
  removedCartItemIndex.value = -1
}

const cartItemTicketCount = (item) => {
  return Object.values(item?.participants ?? {}).reduce((total, count) => total + Number(count || 0), 0)
}

const removeCartItem = (item) => {
  const index = cartItems.value.findIndex((current) => current.id === item.id)

  if (editingCartItemId.value === item.id) {
    editingCartItemId.value = null
  }

  removedCartItem.value = cloneCartItem(item)
  removedCartItemIndex.value = index
  store.removeFromCart(item.id)
  updateCountdown()
}

const adjustCartParticipant = (item, ticket, amount) => {
  if (amount < 0 && cartItemTicketCount(item) <= 1) {
    removeCartItem(item)
    return
  }

  store.adjustParticipant(item.id, ticket.key, amount)
}

const undoRemoveCartItem = async () => {
  if (!removedCartItem.value) {
    return
  }

  store.restoreCartItem(cloneCartItem(removedCartItem.value), removedCartItemIndex.value)
  clearRemovedCartItem()
  await store.loadUpsells('cart')
  updateCountdown()
}

const beginAddExperience = () => {
  editingCartItemId.value = null
  addingExperience.value = true
  selectedAddAttractionCode.value = false
  attractionPickerOpen.value = true
  inlineBookingKey.value += 1
}

const cancelAddExperience = () => {
  addingExperience.value = false
  selectedAddAttractionCode.value = false
  attractionPickerOpen.value = false
  inlineBookingKey.value += 1
}

const toggleAttractionPicker = () => {
  attractionPickerOpen.value = !attractionPickerOpen.value
}

const chooseAddAttraction = async (attraction) => {
  if (!attraction?.code) {
    return
  }

  editingCartItemId.value = null
  selectedAddAttractionCode.value = attraction.code
  attractionPickerOpen.value = false
  inlineBookingKey.value += 1

  await nextTick()
  document.getElementById('cart-inline-booking')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

const handleInlineAdded = async () => {
  addingExperience.value = false
  selectedAddAttractionCode.value = false
  attractionPickerOpen.value = false
  inlineBookingKey.value += 1
  await store.loadUpsells('cart')
  updateCountdown()

  document.getElementById('cart-top')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

const editCartItem = async (item) => {
  if (!item?.id) {
    return
  }

  addingExperience.value = false
  selectedAddAttractionCode.value = false
  attractionPickerOpen.value = false
  editingCartItemId.value = item.id
  inlineBookingKey.value += 1

  await nextTick()
  document.getElementById(`cart-edit-booking-${item.id}`)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

const cancelEditCartItem = () => {
  editingCartItemId.value = null
  inlineBookingKey.value += 1
}

const handleInlineUpdated = async () => {
  editingCartItemId.value = null
  inlineBookingKey.value += 1
  await store.loadUpsells('cart')
  updateCountdown()
}

const handleFullscreenMode = async (state) => {
  fullscreenMode.value = state
}

const goBack = () => {
  window.history.back()
}

const loadCart = async () => {
  catalogLoadFailed.value = false

  try {
    await store.ensureLoaded()
  } catch (error) {
    catalogLoadFailed.value = true
    reportLoadError(error)
    return
  }

  catalogLoading.value = false
  store.pruneUnavailableCart()
  syncCartMessages()
  store.pruneExpiredCart()
  await store.loadUpsells('cart')
  updateCountdown()

  if (props.code) {
    //const matched = store.attractions.find((att) => att.code === props.code);
    const matched = selectableAttractions.value.find((att) => att.code === props.code);
    if (matched) {
      selectedAddAttractionCode.value = matched.code
    }
  }

  if (!countdownInterval) {
    countdownInterval = window.setInterval(updateCountdown, 1000)
  }
}

onMounted(async () => {
  document.body.classList.add(mobileFooterHiddenClass)
  zohoChat.setVisibility(false)
  await loadCart()
})

watch(cartUpsellSignature, async () => {
  await store.loadUpsells('cart')
})

onBeforeUnmount(() => {
  document.body.classList.remove(mobileFooterHiddenClass)
  zohoChat.setVisibility(true)

  if (countdownInterval) {
    window.clearInterval(countdownInterval)
  }
})
</script>
